/**
 * 顧客検索モーダル (T3 / v3.2.3)。
 *
 * 商談の「紹介者」「親 (所有者)」フィールドで顧客を選択するときに使う。
 * - 検索キー: 氏名 / メール / contactId
 * - 検索モード: 全件一致 (exact) / 部分一致 (substring) / キーワード一致 (split)
 * - 選択: クリックで onSelect(contactId) を呼んでクローズ
 *
 * デザイン方針:
 *   - useMailContacts() で全件 (max 500) ロード後、クライアント側でフィルタ
 *   - 大規模化したら Cloud Function 検索に切り替える前提
 */
import type { MailContactRecord } from '@/hooks/useMailContacts';
import { Search, User, Building2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useMailContacts } from '@/hooks/useMailContacts';

type MatchMode = 'exact' | 'substring' | 'keyword';

const MODE_OPTIONS: Array<{ value: MatchMode; label: string }> = [
  { value: 'substring', label: '部分一致' },
  { value: 'keyword', label: 'キーワード一致' },
  { value: 'exact', label: '完全一致' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 選択したときに呼ばれるコールバック (contactId) */
  onSelect: (contactId: string) => void;
  title?: string;
  description?: string;
  /** 除外したい contactId (現在の顧客自身など) */
  excludeContactId?: string | null;
}

export function ContactPickerDialog({
  open,
  onOpenChange,
  onSelect,
  title = '顧客を選択',
  description = '氏名・メール・ID で検索',
  excludeContactId,
}: Props) {
  const contacts = useMailContacts({ limit: 500 });
  const [keyword, setKeyword] = useState('');
  const [mode, setMode] = useState<MatchMode>('substring');

  const filtered = useMemo(() => {
    const list = contacts.data ?? [];
    const filteredByExclude = excludeContactId
      ? list.filter((c) => c.id !== excludeContactId)
      : list;
    const k = keyword.trim();
    if (!k) return filteredByExclude.slice(0, 50);
    return matchContacts(filteredByExclude, k, mode).slice(0, 100);
  }, [contacts.data, keyword, mode, excludeContactId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* 検索ボックス */}
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[var(--text-muted)]" />
              <Input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="氏名 / メール / contactId で検索"
                className="pl-9"
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-[var(--text-muted)]">検索モード</Label>
              <div className="flex gap-1">
                {MODE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setMode(opt.value)}
                    className={`px-2 py-0.5 text-[11px] rounded border transition-colors ${
                      mode === opt.value
                        ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                        : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 結果リスト */}
          {contacts.isLoading ? (
            <Spinner />
          ) : filtered.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] italic py-4 text-center">
              該当する顧客がいません
            </p>
          ) : (
            <div className="space-y-1 max-h-[60vh] overflow-y-auto">
              {filtered.map((c) => (
                <ContactRow
                  key={c.id}
                  contact={c}
                  onClick={() => {
                    onSelect(c.id);
                    onOpenChange(false);
                  }}
                />
              ))}
              {keyword.trim() === '' && (
                <p className="text-[10px] text-[var(--text-muted)] italic pt-2 text-center">
                  最新 50 件を表示中。検索ボックスでさらに絞り込みできます。
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ContactRow({
  contact,
  onClick,
}: {
  contact: MailContactRecord;
  onClick: () => void;
}) {
  const isBtoB = contact.kind === 'btob';
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] transition-colors"
    >
      <div className="flex items-center gap-2">
        {isBtoB ? (
          <Building2 className="size-3.5 text-[var(--text-muted)] shrink-0" />
        ) : (
          <User className="size-3.5 text-[var(--text-muted)] shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {contact.name || '(氏名未設定)'}
            {contact.companyName && (
              <span className="text-[11px] text-[var(--text-muted)] ml-2">
                {contact.companyName}
              </span>
            )}
          </div>
          <div className="text-[11px] text-[var(--text-muted)] truncate">
            {contact.defaultEmail || '(メール未設定)'}
            <span className="ml-2 font-mono text-[10px]">{contact.id}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────
// 検索ロジック (純関数。外部からテスト可能にしておく)
// ─────────────────────────────────────────────

function matchContacts(
  list: MailContactRecord[],
  rawKeyword: string,
  mode: MatchMode,
): MailContactRecord[] {
  const k = rawKeyword.trim().toLowerCase();
  if (!k) return list;

  const haystack = (c: MailContactRecord): string[] =>
    [
      c.name ?? '',
      c.defaultEmail ?? '',
      c.id,
      c.companyName ?? '',
      ...(c.emails ?? []).map((e) => e.address),
    ].map((s) => s.toLowerCase());

  if (mode === 'exact') {
    return list.filter((c) => haystack(c).some((h) => h === k));
  }
  if (mode === 'substring') {
    return list.filter((c) => haystack(c).some((h) => h.includes(k)));
  }
  // keyword: 半角・全角スペース区切りの全トークン AND
  const tokens = k.split(/[\s　]+/).filter(Boolean);
  if (tokens.length === 0) return list;
  return list.filter((c) => {
    const hs = haystack(c).join(' ');
    return tokens.every((t) => hs.includes(t));
  });
}
